import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery
} from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LastPageIcon from '@mui/icons-material/LastPage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { formatEuro } from '../../functions/formatNumber';
import { ConcepteIcon } from '../common/IconMap';
import ReglaAssignacioDialog from './ReglaAssignacioDialog';

/**
 * ReglesAssignacioSection
 * Visualitza i gestiona les regles avançades d'assignació d'autoconcepte
 * (autoconcepte.regles-assignacio) amb vista de taula en escriptori i
 * acordions desplegables en mòbil.
 */
const ReglesAssignacioSection = ({
  rules = {},
  onChange,
  conceptesList = [],
  disabled = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Diàlegs d'edició / creació
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const ruleKeys = Object.keys(rules || {});

  // Toggle ràpid d'activació / desactivació d'una regla
  const handleToggleRuleEnabled = (key) => {
    const current = rules[key];
    if (!current) return;

    const updated = {
      ...rules,
      [key]: {
        ...current,
        enabled: !(current.enabled ?? true)
      }
    };
    onChange(updated);
  };

  // Obrir diàleg de creació
  const handleOpenCreate = () => {
    setEditingRule(null);
    setDialogOpen(true);
  };

  // Obrir diàleg d'edició
  const handleOpenEdit = (key) => {
    const target = rules[key];
    if (!target) return;
    setEditingRule({
      ...target,
      nom: target.nom || key
    });
    setDialogOpen(true);
  };

  // Desar regla des del modal (creació o edició)
  const handleSaveRule = (ruleData, previousNom) => {
    const key = ruleData.nom;
    const updated = { ...rules };

    // Si ha canviat la clau en editar, esborrem l'antiga
    if (previousNom && previousNom !== key) {
      delete updated[previousNom];
    }

    updated[key] = ruleData;
    onChange(updated);
    setDialogOpen(false);
    setEditingRule(null);
  };

  // Eliminació directa de regla de la llista (es confirmarà en desar a BBDD si n'hi ha)
  const handleDeleteRule = (key) => {
    const updated = { ...rules };
    delete updated[key];
    onChange(updated);
  };

  return (
    <Card variant="outlined" sx={{ mt: 4, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
      {/* Capçalera de la Secció amb el mateix estil que els blocs 1 i 2 */}
      <CardHeader
        avatar={<EventRepeatIcon color="primary" sx={{ fontSize: 28 }} />}
        title={
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            3. Regles Avançades d'Assignació (regles-assignacio)
          </Typography>
        }
        subheader="Assignació automàtica de concepte i descripció per a moviments periòdics segons text, import, dia del mes i marge de dies."
        action={
          !disabled && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ mr: { xs: 0, sm: 1 }, mt: 1, width: { xs: '100%', sm: 'auto' } }}
            >
              Nova Regla Avançada
            </Button>
          )
        }
        sx={{
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #f0f0f0',
          flexWrap: 'wrap',
          '& .MuiCardHeader-action': { m: 0, mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' } }
        }}
      />

      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        {ruleKeys.length === 0 ? (
          <Alert severity="info">
            No hi ha cap regla avançada d'assignació configurada per a aquest perfil. Prem "Nova Regla Avançada" per crear-ne una.
          </Alert>
        ) : isMobile ? (
          /* ======================================================== */
          /* Vista MÒBIL: Acordions desplegables per a cada regla    */
          /* ======================================================== */
          <Box>
            {ruleKeys.map((key) => {
              const rule = rules[key] || {};
              const isEnabled = rule.enabled ?? true;
              const ruleNom = rule.nom || key;
              const ruleImport = rule.import !== undefined ? rule.import : (rule.importe !== undefined ? rule.importe : 0);
              const isUltimDia = Boolean(rule['ultim-dia']);
              const tolDies = rule['tolerancia-dies'] !== undefined
                ? rule['tolerancia-dies']
                : (rule.toleranciaDies !== undefined ? rule.toleranciaDies : 0);

              return (
                <Accordion
                  key={key}
                  sx={{
                    mb: 1.5,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px !important',
                    boxShadow: 'none',
                    opacity: isEnabled ? 1 : 0.65,
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: isEnabled ? '#fcfcfc' : '#f5f5f5',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      px: 1.5
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 0.5, flexWrap: 'nowrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        <ConcepteIcon concept={rule.concepte} onlyIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: isEnabled ? '#1e293b' : '#94a3b8',
                            fontFamily: 'monospace',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {ruleNom}
                        </Typography>
                        {!isEnabled && (
                          <Chip label="OFF" size="small" sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }} />
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                        <Chip
                          label={formatEuro(ruleImport)}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: ruleImport < 0 ? '#b91c1c' : '#15803d',
                            bgcolor: ruleImport < 0 ? '#fee2e2' : '#dcfce7'
                          }}
                        />

                        {!disabled && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEdit(key);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRule(key);
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 2.5, pb: 2, bgcolor: '#ffffff', borderTop: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">Identificador:</Typography>
                        <Chip label={ruleNom} size="small" sx={{ fontFamily: 'monospace', fontWeight: 600 }} />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">Moviment bancari:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {rule.moviment}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">Planificació:</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {isUltimDia ? (
                            <Chip icon={<LastPageIcon sx={{ fontSize: 16 }} />} label="Últim dia" size="small" color="secondary" variant="outlined" />
                          ) : (
                            <Chip icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />} label={`Dia ${rule.dia ?? '—'}`} size="small" color="primary" variant="outlined" />
                          )}
                          {tolDies > 0 && <Chip label={`±${tolDies}d`} size="small" sx={{ bgcolor: '#e2e8f0', fontSize: '0.7rem' }} />}
                        </Box>
                      </Box>

                      {rule.descripcio && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Descripció complementària:</Typography>
                          <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#475569' }}>
                            {rule.descripcio}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        ) : (
          /* ======================================================== */
          /* Vista ESCRIPTORI: Taula completa amb totes les dades      */
          /* ======================================================== */
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Table size="small" aria-label="taula de regles avançades">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 80 }}>Estat</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Identificador</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Moviment Bancari</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Import</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Planificació</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Concepte i Descripció</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 100 }}>Accions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ruleKeys.map((key) => {
                  const rule = rules[key] || {};
                  const isEnabled = rule.enabled ?? true;
                  const ruleNom = rule.nom || key;
                  const ruleImport = rule.import !== undefined ? rule.import : (rule.importe !== undefined ? rule.importe : 0);
                  const isUltimDia = Boolean(rule['ultim-dia']);
                  const tolDies = rule['tolerancia-dies'] !== undefined
                    ? rule['tolerancia-dies']
                    : (rule.toleranciaDies !== undefined ? rule.toleranciaDies : 0);

                  return (
                    <TableRow
                      key={key}
                      hover
                      sx={{
                        opacity: isEnabled ? 1 : 0.6,
                        transition: 'opacity 0.2s ease',
                        '&:last-child td, &:last-child th': { border: 0 }
                      }}
                    >
                      {/* Estat Switch */}
                      <TableCell>
                        <Tooltip title={isEnabled ? 'Regla activa' : 'Regla desactivada'}>
                          <Switch
                            size="small"
                            checked={isEnabled}
                            onChange={() => handleToggleRuleEnabled(key)}
                            color="success"
                            disabled={disabled}
                          />
                        </Tooltip>
                      </TableCell>

                      {/* Identificador / Nom */}
                      <TableCell>
                        <Chip
                          label={ruleNom}
                          size="small"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            bgcolor: '#f1f5f9',
                            color: '#334155'
                          }}
                        />
                      </TableCell>

                      {/* Text Moviment */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {rule.moviment}
                        </Typography>
                      </TableCell>

                      {/* Import */}
                      <TableCell align="right">
                        <Chip
                          label={formatEuro(ruleImport)}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            color: ruleImport < 0 ? '#b91c1c' : '#15803d',
                            bgcolor: ruleImport < 0 ? '#fee2e2' : '#dcfce7'
                          }}
                        />
                      </TableCell>

                      {/* Planificació (dia / últim dia + tolerància) */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          {isUltimDia ? (
                            <Chip
                              icon={<LastPageIcon sx={{ fontSize: 16 }} />}
                              label="Últim dia"
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          ) : (
                            <Chip
                              icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
                              label={`Dia ${rule.dia ?? '—'}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          )}

                          {tolDies > 0 && (
                            <Tooltip title={`Tolerància de marge: ±${tolDies} dies per cap de setmana o festius`}>
                              <Chip
                                label={`±${tolDies}d`}
                                size="small"
                                sx={{ bgcolor: '#e2e8f0', fontSize: '0.7rem', height: 20 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      {/* Concepte i Descripció */}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <ConcepteIcon concept={rule.concepte} sx={{ fontSize: 18 }} />
                          </Box>
                          {rule.descripcio && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', pl: 3 }}>
                              {rule.descripcio}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* Accions */}
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="Editar regla">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenEdit(key)}
                                disabled={disabled}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Eliminar regla">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteRule(key)}
                                disabled={disabled}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Diàleg de Creació / Edició */}
      <ReglaAssignacioDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingRule(null);
        }}
        onSave={handleSaveRule}
        initialRule={editingRule}
        existingKeys={ruleKeys}
        conceptesList={conceptesList}
      />
    </Card>
  );
};

export default ReglesAssignacioSection;
